import Image from "next/image";
import { sanitizeHTML } from "@/lib/utils";
import { getReadableImageUrl } from "@/lib/directus";

interface GoodCandidateData {
  images?: {
    images_id: {
      alt: string;
      image_file: {
        image: {
          id: string;
          filename_download: string;
        };
      };
    };
  }[];
  content_collection?: {
    id: string;
    site_content_collections_id: {
      collection: {
        item: {
          title: string;
          text: string;
        };
      }[];
    };
  }[];
}

export const fields = [
  "images.images_id.alt",
  "images.images_id.image_file.image.id",
  "images.images_id.image_file.image.filename_download",

  "content_collection.id",
  "content_collection.site_content_collections_id.collection.item:site_content.title",
  "content_collection.site_content_collections_id.collection.item:site_content.text",
] as const;

interface GoodCandidateProps {
  data?: GoodCandidateData;
}

export default function GoodCandidate({ data }: GoodCandidateProps) {
  const bgImage = data?.images?.[0];
  const bgImageId = bgImage?.images_id?.image_file?.image?.id;
  const bgImageFilename =
    bgImage?.images_id?.image_file?.image?.filename_download;
  const bgImageAlt = bgImage?.images_id?.alt;

  return (
    <section className="w-full bg-white relative">
      <div className="absolute -left-[50%] top-0 h-full w-full hidden lg:block">
        <Image
          src="/images/yellow.svg"
          alt="Background Pattern"
          fill
          className="object-contain opacity-50"
          sizes="50vw"
          quality={65}
        />
      </div>
      <div className="relative mx-auto py-8 xl:py-32">
        <div className="relative flex flex-col lg:flex-row">
          <div className="relative w-full lg:w-1/2 h-[460px] lg:h-auto overflow-hidden order-first lg:order-last">
            <div className="absolute left-0 lg:left-0 top-0 w-full h-full lg:rounded-tl-[400px] lg:rounded-bl-[400px] overflow-hidden">
              {bgImageId && bgImageFilename ? (
                <Image
                  src={getReadableImageUrl(bgImageId, bgImageFilename)}
                  alt={bgImageAlt || ""}
                  fill
                  className="object-cover transition-transform duration-300 hover:scale-[1.5]"
                  sizes="(max-width: 1024px) 100vw, 50vw"
                  quality={65}
                />
              ) : (
                <Image
                  src="/images/image56.png"
                  alt="Rhinoplasty Candidate"
                  fill
                  className="object-cover transition-transform duration-300 hover:scale-[1.5]"
                  sizes="(max-width: 1024px) 100vw, 50vw"
                  quality={65}
                />
              )}
            </div>
          </div>
          <div className="w-full lg:w-1/2 p-8 lg:p-20 pb-0 space-y-8">
            <div className="space-y-8">
              <h2 className="text-[2rem] font-essonnes font-light leading-tight md:text-5xl lg:text-[3.375rem] text-primary">
                {data?.content_collection &&
                  data.content_collection.length > 0 &&
                  data.content_collection[0]?.site_content_collections_id?.collection.find(
                    (item) => item.item.title
                  )?.item?.title}
              </h2>
              {data?.content_collection &&
                data.content_collection.length > 0 && (
                  <div
                    className="md:font-light md:leading-[1.6rem] md:text-[1.25rem] text-[1rem]! font-normal! leading-normal! font-greycliff text-primary tracking-tight
                      [&>p]:pb-4 [&>p:last-child]:pb-0
                      [&>ul]:list-disc [&>ul]:pl-6 [&>ul]:pb-4 [&>ul>li]:mb-2 [&>ul>li:last-child]:mb-0
                      [&>ol]:list-decimal [&>ol]:pl-6 [&>ol>li]:mb-2 [&>ol>li:last-child]:mb-0
                      md:[&>*]:font-light md:[&>*]:leading-[1.6rem] md:[&>*]:text-[1.25rem]
                      [&>*]:text-[1rem]! [&>*]:font-normal! [&>*]:leading-normal!
                      [&>h2]:text-[2rem] [&>h2]:font-essonnes [&>h2]:font-light [&>h2]:leading-tight md:[&>h2]:text-5xl lg:[&>h2]:text-[3.375rem] [&>h2]:text-primary
                      [&_a]:underline [&_a]:transition-all [&_a:hover]:no-underline"
                    dangerouslySetInnerHTML={sanitizeHTML(
                      data.content_collection[0]?.site_content_collections_id?.collection.find(
                        (item) => item.item.text
                      )?.item?.text || ""
                    )}
                  />
                )}
            </div>
          </div>
        </div>
        <div className="flex flex-col lg:flex-row px-8 lg:px-20 pt-8 lg:pt-16 gap-8 lg:gap-24">
          {data?.content_collection?.slice(1).map((collection, index) => {
            const titleItem =
              collection?.site_content_collections_id?.collection.find(
                (item) => item.item.title
              )?.item;
            const textItem =
              collection?.site_content_collections_id?.collection.find(
                (item) => item.item.text
              )?.item;

            return (
              <div
                key={collection.id || index}
                className="w-full lg:w-1/2 space-y-8"
              >
                <h2 className="text-[2rem] font-essonnes font-light leading-tight md:text-5xl lg:text-[3.375rem] text-primary">
                  {titleItem?.title}
                </h2>
                <div className="space-y-6">
                  <div
                    className="md:font-light md:leading-[1.6rem] md:text-[1.25rem] text-[1rem] font-normal leading-normal font-greycliff text-primary tracking-tight [&>p]:pb-4 [&>p:last-child]:pb-0 [&>ul]:list-disc [&>ul]:pl-6 [&>ul]:pb-4 [&>ul>li]:mb-2 [&>ul>li:last-child]:mb-0 [&>ol]:list-decimal [&>ol]:pl-6 [&>ol>li]:mb-2 [&>ol>li:last-child]:mb-0 md:[&>*]:font-light md:[&>*]:leading-[1.6rem] md:[&>*]:text-[1.25rem] [&>*]:text-[1rem]! [&>*]:font-normal! [&>*]:leading-normal! [&>h2]:text-[2rem] [&>h2]:font-essonnes [&>h2]:font-light [&>h2]:leading-tight md:[&>h2]:text-5xl lg:[&>h2]:text-[3.375rem] [&>h2]:text-primary [&_a]:underline [&_a]:transition-all [&_a:hover]:no-underline"
                    dangerouslySetInnerHTML={sanitizeHTML(textItem?.text || "")}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
